import { chain } from 'lodash';
import { JSX } from 'react';

// eslint-disable-next-line import/no-cycle
import { Action, ActionAttr } from './action';
import { RouteAttr } from './types';

export type ScopeAttr = RouteAttr & {
  /** React element that the scope will call every time a nested path is called */
  element?: JSX.Element;
  /**
   * Define if the nested path will require a param present in the URL
   *
   * With:
   * `/author/:authorId/book/:id`
   *
   * Without:
   * `/author/book/:id`
   */
  member?: boolean;
  /**
   * Overwrite the generated path
   *
   * This can be usefull if you want to have scope that validate the state of the current user for example
   */
  path?: string;
};

export class Scope {
  public name: ScopeAttr['name'];
  public param: ScopeAttr['param'];
  public actions: Action[];
  public parent: Scope | null;
  public scopes: Scope[];
  public member: boolean;
  public element?: JSX.Element;
  public path?: string;

  constructor(attributes: ScopeAttr, parent: Scope | null = null) {
    this.name = attributes.name;
    this.param = attributes.param;
    this.parent = parent;
    this.actions = [];
    this.scopes = [];
    this.member = attributes.member ?? false;
    this.element = attributes.element;
    this.path = attributes.path;
  }

  public fetchActions(): Action[] {
    const subActions = chain(this.scopes)
      .map((scope: Scope) => scope.fetchActions())
      .flatten()
      .value();

    return this.actions.concat(subActions);
  }

  /**
   * Build a scope on top of a scope
   *
   * @param attributes `ScopeAttr`
   */
  public scope(attributes: ScopeAttr, nestedFunction: (scope: Scope) => void): void {
    const nestedScope = new Scope(attributes, this);
    nestedFunction(nestedScope);

    this.scopes.push(nestedScope);
  }

  /**
   * Build action on top of a scope
   *
   * @param attributes `ActionAttr`
   */
  public action(attributes: ActionAttr): void {
    const action = new Action(attributes, this);

    this.actions.push(action);
  }
}
